import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { registerGlobalHeader } from "../api";

interface AuthContextType {
	isAuthenticated: boolean;
	user: any;
	login: (username: string, password: string) => Promise<void>;
	registerUser: (
		username: string,
		password: string,
		email: string,
		firstName: string,
		lastName: string,
		linkedinUrl: string,
		companyId: number,
		phoneNumber?: string,
		linkedinEmail?: string
	) => Promise<string>;
	logout: () => void;
	refreshToken: () => Promise<void>;
	impersonate: (username: string) => Promise<void>;
	stopImpersonating: () => Promise<void>;
	isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Resolved during the first render, not in a mount effect: an effect leaves the
// first render unauthenticated, so the tree redirects to /login, the login
// screen sees the flag flip and navigates straight back, and the remount runs
// every startup fetch a second time.
const resolveStoredAuthentication = (): boolean => {
	const token = localStorage.getItem("access_token");
	if (!token) {
		return false;
	}
	// For DRF Token auth (40 char hex), use "Token" prefix
	// For JWT (longer tokens with dots), use "Bearer" prefix
	const authPrefix = token.length === 40 && !/\./.test(token) ? "Token" : "Bearer";
	registerGlobalHeader("Authorization", `${authPrefix} ${token}`);
	return true;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
		resolveStoredAuthentication
	);
	const [user, setUser] = useState<any>(null);
	const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
	const navigate = useNavigate();

	const refreshToken = async () => {
		try {
			console.debug("[Auth Context] Starting token refresh");
			const refresh = localStorage.getItem("refresh_token");
			if (!refresh) {
				console.debug("[Auth Context] No refresh token found");
				throw new Error("No refresh token available");
			}

			console.debug("[Auth Context] Attempting to refresh token with API");
			const response = await fetch("/api/v1/auth/refresh/", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ refresh }),
			});

			if (!response.ok) {
				console.debug("[Auth Context] Token refresh failed", {
					status: response.status,
					statusText: response.statusText,
				});
				throw new Error("Token refresh failed");
			}

			const data = await response.json();
			console.debug("[Auth Context] Token refresh successful");
			localStorage.setItem("access_token", data.access);
			if (data.refresh) {
				console.debug("[Auth Context] Got new refresh token, updating storage");
				localStorage.setItem("refresh_token", data.refresh);
			}
			registerGlobalHeader("Authorization", `Bearer ${data.access}`);
			return data.access;
		} catch (error) {
			console.debug("[Auth Context] Error during refresh, logging out", {
				error,
			});
			// If refresh fails, log out the user
			logout();
			throw error;
		}
	};

	useEffect(() => {
		// Check if we have original tokens (means we're impersonating)
		const originalToken = localStorage.getItem("original_access_token");
		if (originalToken) {
			setIsImpersonating(true);
		}
	}, []);

	const login = async (username: string, password: string) => {
		try {
			console.debug("[Auth Context] Attempting login");

			const response = await fetch("/api/v2/auth/login/", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				// Don't send cookies with the request
				credentials: "omit",
				body: JSON.stringify({ username, password }),
			});

			if (!response.ok) {
				console.debug("[Auth Context] Login failed", {
					status: response.status,
					statusText: response.statusText,
				});

				// Get the error data from the response
				let errorMessage = "Login failed";
				try {
					const errorData = await response.json();
					console.warn("[Auth Context] Login error response:", errorData);

					// Set the error message from the response
					if (errorData.error) {
						errorMessage = errorData.error;
						console.warn(
							"[Auth Context] Using error message from backend:",
							errorMessage
						);
					} else if (errorData.detail) {
						errorMessage = errorData.detail;
						console.warn(
							"[Auth Context] Using detail message from backend:",
							errorMessage
						);
					}
				} catch (parseError) {
					console.warn(
						"[Auth Context] Could not parse error response:",
						parseError
					);
				}

				// Create a simple error with the message from the backend
				console.warn(
					"[Auth Context] Throwing error with message:",
					errorMessage
				);
				throw new Error(errorMessage);
			}

			const data = await response.json();
			console.debug("[Auth Context] Login successful");
			localStorage.setItem("access_token", data.access);
			localStorage.setItem("refresh_token", data.refresh);
			registerGlobalHeader("Authorization", `Bearer ${data.access}`);
			setUser(data.user);
			setIsAuthenticated(true);

			// Redirect to root after successful login
			navigate("/");
		} catch (error) {
			console.debug("[Auth Context] Login error", { error });
			throw error;
		}
	};

	const registerUser = async (
		username: string,
		password: string,
		email: string,
		firstName: string,
		lastName: string,
		linkedinUrl: string,
		companyId: number,
		phoneNumber?: string,
		linkedinEmail?: string
	) => {
		try {
			const registerData = {
				username,
				password,
				email,
				first_name: firstName,
				last_name: lastName,
				linkedin_url: linkedinUrl,
				company_id: companyId,
				phone_number: phoneNumber,
				linkedin_email: linkedinEmail,
			};

			console.debug("[Auth Context] Attempting registration", { registerData });

			const response = await fetch("/api/v2/auth/register/", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				// Don't send cookies with the request
				credentials: "omit",
				body: JSON.stringify(registerData),
			});

			if (!response.ok) {
				console.debug("[Auth Context] Registration failed", {
					status: response.status,
					statusText: response.statusText,
				});
				const error = await response.json();
				throw new Error(error.error || error.detail || "Registration failed");
			}

			console.debug("[Auth Context] Registration successful");
			// Do not authenticate the user since accounts are now created as inactive
			// and need admin approval before becoming active

			// Return the user's email for displaying in the success message
			return email;
		} catch (error) {
			console.debug("[Auth Context] Registration error", { error });
			throw error;
		}
	};

	const impersonate = async (username: string) => {
		try {
			const response = await fetch("/api/v2/auth/impersonate/", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
				body: JSON.stringify({ username }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Impersonation failed");
			}

			const data = await response.json();

			// Store original tokens
			localStorage.setItem("original_access_token", data.original_access);
			localStorage.setItem("original_refresh_token", data.original_refresh);

			// Set new impersonation tokens
			localStorage.setItem("access_token", data.access);
			localStorage.setItem("refresh_token", data.refresh);

			registerGlobalHeader("Authorization", `Bearer ${data.access}`);
			setUser(data.user);
			setIsImpersonating(true);
		} catch (error) {
			console.debug("[Auth Context] Impersonation error", { error });
			throw error;
		}
	};

	const stopImpersonating = async () => {
		try {
			// Restore original tokens
			const originalAccess = localStorage.getItem("original_access_token");
			const originalRefresh = localStorage.getItem("original_refresh_token");

			if (!originalAccess || !originalRefresh) {
				throw new Error("No original tokens found");
			}

			localStorage.setItem("access_token", originalAccess);
			localStorage.setItem("refresh_token", originalRefresh);

			// Clean up original tokens
			localStorage.removeItem("original_access_token");
			localStorage.removeItem("original_refresh_token");

			registerGlobalHeader("Authorization", `Bearer ${originalAccess}`);
			setIsImpersonating(false);

			// Use the correct endpoint to refresh user data
			const response = await fetch("/api/v2/user/", {
				headers: {
					Authorization: `Bearer ${originalAccess}`,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to get user data");
			}

			const data = await response.json();
			setUser(data.results[0]); // API returns results array

			// Reload current view instead of navigating to TODO
			window.location.reload();
		} catch (error) {
			console.debug("[Auth Context] Stop impersonation error", { error });
			logout();
			throw error;
		}
	};

	const logout = () => {
		console.debug("[Auth Context] Logging out");
		localStorage.removeItem("access_token");
		localStorage.removeItem("refresh_token");
		localStorage.removeItem("original_access_token");
		localStorage.removeItem("original_refresh_token");
		registerGlobalHeader("Authorization", "");
		setUser(null);
		setIsAuthenticated(false);
		setIsImpersonating(false);
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				user,
				login,
				registerUser,
				logout,
				refreshToken,
				impersonate,
				stopImpersonating,
				isImpersonating,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
